import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { api, fetchUserProfile, UserProfile } from '../services/api';
import { Analytics } from '../services/analytics';
import { Sparkles, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSubscription, PRO_ACCESS_DAYS } from './SubscriptionFeatures';

export const PaymentButton = ({ compact = false }: { compact?: boolean }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePayment = async () => {
        if (isProcessing) return;

        setIsProcessing(true);
        setError(null);

        try {
            Analytics.logEvent('Payment', 'Initiate');
            const response = await api.post('/payments/create-checkout-session/');
            const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

            if (!stripe) {
                throw new Error('Stripe failed to load');
            }

            const { error: stripeError } = await stripe.redirectToCheckout({
                sessionId: response.data.sessionId,
            });

            if (stripeError) {
                throw stripeError;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Payment initialization failed';
            setError(errorMessage);
            Analytics.logError('Payment', errorMessage, 'high');
            console.error('Payment error:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="space-y-4">
            <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="w-full bg-brand-600
                         text-white font-semibold py-3 px-6 rounded-lg
                         hover:bg-brand-700
                         transform transition-all duration-200
                         hover:scale-[1.02] active:scale-[0.98]
                         shadow-md hover:shadow-lg
                         flex items-center justify-center space-x-2
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {isProcessing ? (
                    <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                    </span>
                ) : (
                    <>
                        <span>{compact ? 'Get Pro access' : 'Get PRO - £8.99'}</span>
                        {!compact && <span className="text-sm opacity-75">(one-time payment)</span>}
                    </>
                )}
            </button>

            {!compact && <p className="text-xs text-ink-500 text-center">
                Includes {PRO_ACCESS_DAYS} days of access. Not a subscription - it does not renew.
            </p>}

            {error && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                    {error}
                </div>
            )}
        </div>
    );
};

export const PaymentSuccess = ({ onProfileUpdate }: { onProfileUpdate: (profile: UserProfile) => void }) => {
    const navigate = useNavigate();
    const { checkProStatus } = useSubscription();
    const [isChecking, setIsChecking] = useState(true);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout>;
        let checks = 0;
        setIsChecking(true);
        const check = async () => {
            checks += 1;
            try {
                const active = await checkProStatus();
                if (cancelled) return;
                if (active) {
                    // Refresh the shared profile too: alerts use it to check access.
                    const profile = await fetchUserProfile();
                    if (cancelled) return;
                    if (profile.is_pro) {
                        onProfileUpdate(profile);
                        Analytics.logEvent('Payment', 'Success');
                        navigate('/availability?welcome=1', { replace: true });
                        return;
                    }
                }
            } catch {
                // A delayed webhook or temporary network failure can recover.
            }
            if (cancelled) return;
            if (checks < 8) timer = setTimeout(check, 2000);
            else setIsChecking(false);
        };
        check();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [checkProStatus, navigate, onProfileUpdate, attempt]);

    return (
        <div className="pro-activation" role="status">
            <div className="pro-welcome-seal"><Sparkles size={30} /></div>
            <p className="pro-eyebrow">Eatsy Pro</p>
            <h1 className="font-display text-4xl">{isChecking ? 'Getting your pass ready' : 'Your pass needs a moment'}</h1>
            <p className="mt-4 text-ink-600 leading-relaxed">
                {isChecking ? 'We’re confirming your Pro access. Your personal introduction is up next.' : 'We haven’t been able to confirm your access yet. Payment confirmation can take a little longer. Check again in a moment; you don’t need to pay again.'}
            </p>
            {isChecking ? <div className="pro-activation-progress" aria-label="Confirming access" /> : (
                <div className="mt-6 flex flex-col items-center gap-4">
                    <button className="pro-tour-next" onClick={() => setAttempt(value => value + 1)}>Check again</button>
                    <Link to="/availability" className="text-sm underline text-brand-700">Return to availability</Link>
                </div>
            )}
        </div>
    );
};

export const PaymentCancel = () => {
    useEffect(() => {
        Analytics.logEvent('Payment', 'Cancelled');
    }, []);

    return (
        <div className="max-w-lg mx-auto mt-12 p-6 bg-surface rounded-lg shadow-lg text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="font-display font-normal tracking-tight text-2xl text-ink-900 mb-2">Payment Cancelled</h2>
            <p className="text-ink-600 mb-4">
                Your payment was cancelled. No charges were made.
            </p>
            <Link
                to="/availability"
                className="inline-block text-brand-600 hover:text-brand-800 font-medium"
            >
                Return to availability
            </Link>
        </div>
    );
};
